import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Modal, Radio, Space, Table, Tag, Typography, notification, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { ReplayCollect, ReplayCollectGame } from '../api/go';

type CreateForm = {
  game_id: string;
  remark?: string;
};

const collectUserOptions = [
  {
    name: '剪辑',
    id: 'a97f677a00c6497da3f2dfd838ae379e',
  },
  {
    name: '共享',
    id: '6c5dc5b0586148d9af353f97a1f015a9',
  },
];

const defaultCollectUserId = collectUserOptions[0].id;

function getErrorMessage(err: any) {
  if (err?.status) {
    return `请求失败: ${err.status}`;
  }
  if (err?.message) {
    return err.message;
  }
  return '请求失败';
}

function normalizeGameId(gameId?: string) {
  return (gameId || '').trim();
}

export default function ReplayCollectPage() {
  const [listLoading, setListLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [collectUserId, setCollectUserId] = useState(defaultCollectUserId);
  const [records, setRecords] = useState<ReplayCollectGame[]>([]);
  const [form] = Form.useForm<CreateForm>();

  async function loadList() {
    setListLoading(true);
    try {
      const list = await ReplayCollect.list(collectUserId);
      setRecords(list);
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setListLoading(false);
    }
  }

  useEffect(() => {
    loadList();
  }, [collectUserId]);

  const collectGameIdSet = useMemo(() => new Set(records.map((record) => record.game_id)), [records]);

  async function createCollect(values: CreateForm) {
    const gameId = normalizeGameId(values.game_id);
    if (!/^\d+$/.test(gameId)) {
      message.error('请输入正确的 game_id');
      return;
    }

    if (collectGameIdSet.has(gameId)) {
      message.warning('该对局已经在收藏列表中');
      return;
    }

    setCreateLoading(true);
    try {
      await ReplayCollect.create({
        user_id: collectUserId,
        game_id: gameId,
        remark: (values.remark || '').trim(),
      });
      notification.success({ message: '添加成功' });
      setCreateOpen(false);
      form.resetFields();
      await loadList();
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setCreateLoading(false);
    }
  }

  const columns: ColumnsType<ReplayCollectGame> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 90,
      sorter: (left, right) => right.id - left.id,
    },
    {
      title: '对局信息',
      dataIndex: 'game_id',
      width: 180,
      render: (gameId: string, row: ReplayCollectGame) => {
        return (
          <div>
            <div>
              <Typography.Text copyable={{ text: gameId }}>{gameId}</Typography.Text>
            </div>
            <div>
              <Typography.Text copyable={{ text: row.game_name }}>{row.game_name}</Typography.Text>
            </div>
            <div>
              <Space size="small">
                {row.match_type ? <Tag color="blue">{row.match_type}</Tag> : null}
                {row.room_type ? <Tag>{row.room_type}</Tag> : null}
                {!row.match_type && !row.room_type ? <Typography.Text type="secondary">-</Typography.Text> : null}
              </Space>
            </div>
          </div>
        );
      },
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 180,
      ellipsis: true,
      render: (value: string) => value || <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      width: 180,
      render: (value: string) => value || <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: '收藏时间',
      dataIndex: 'created_at',
      width: 180,
    },
  ];

  return (
    <div style={{ textAlign: 'left' }}>
      <Card
        title={
          <Space>
            <span>收藏管理</span>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={collectUserId}
              options={collectUserOptions.map((item) => ({ label: item.name, value: item.id }))}
              onChange={(event) => setCollectUserId(event.target.value)}
              disabled={listLoading || createLoading}
            />
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadList} loading={listLoading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              添加收藏
            </Button>
          </Space>
        }
      >
        <Table<ReplayCollectGame>
          rowKey="id"
          loading={listLoading}
          columns={columns}
          dataSource={records}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1280 }}
        />
      </Card>

      <Modal
        title="添加收藏"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createLoading}
        destroyOnHidden
      >
        <Form<CreateForm> form={form} layout="vertical" onFinish={createCollect} preserve={false}>
          <Form.Item<CreateForm>
            label="Game ID"
            name="game_id"
            rules={[
              { required: true, message: '请输入 game_id' },
              { pattern: /^\d+$/, message: 'game_id 只能包含数字' },
            ]}
          >
            <Input autoFocus placeholder="请输入 game_id" disabled={createLoading} />
          </Form.Item>
          <Form.Item<CreateForm> label="备注" name="remark" rules={[{ max: 64, message: '备注不能超过 64 个字符' }]}>
            <Input placeholder="可选" disabled={createLoading} maxLength={64} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
